// Sector packages API wrappers — M13.

import type {
  CreateSectorPackageRequest,
  PublishedSectorPackagesResponse,
  ResolvedSectorPackage,
  SectorPackageEditabilityResponse,
  SectorPackageSummary,
  SectorPackagesListResponse,
  UpdateSectorPackageRequest,
  UpgradeSectorPackageRequest,
  UpgradeSectorPackageResponse,
} from "@mahir/shared";
import { api } from "./client";

export async function getSectorPackageEditability(
  id: string,
): Promise<SectorPackageEditabilityResponse> {
  return api<SectorPackageEditabilityResponse>(`/admin/sector-packages/${id}/editability`);
}

export interface ListSectorPackagesFilter {
  status?: "draft" | "published" | "archived";
  parentFrameworkVersionId?: string;
  parentFrameworkId?: string;
}

function buildQuery(f: ListSectorPackagesFilter): string {
  const params = new URLSearchParams();
  if (f.status) params.set("status", f.status);
  if (f.parentFrameworkVersionId)
    params.set("parentFrameworkVersionId", f.parentFrameworkVersionId);
  if (f.parentFrameworkId) params.set("parentFrameworkId", f.parentFrameworkId);
  const qs = params.toString();
  return qs ? `?${qs}` : "";
}

export async function listSectorPackages(
  filter: ListSectorPackagesFilter = {},
): Promise<SectorPackagesListResponse> {
  return api<SectorPackagesListResponse>(`/admin/sector-packages${buildQuery(filter)}`);
}

export async function getSectorPackage(id: string): Promise<{ package: SectorPackageSummary }> {
  return api<{ package: SectorPackageSummary }>(`/admin/sector-packages/${id}`);
}

export async function getResolvedSectorPackage(id: string): Promise<ResolvedSectorPackage> {
  return api<ResolvedSectorPackage>(`/admin/sector-packages/${id}/resolved`);
}

export async function createSectorPackage(
  body: CreateSectorPackageRequest,
): Promise<{ package: SectorPackageSummary }> {
  return api<{ package: SectorPackageSummary }>("/admin/sector-packages", {
    method: "POST",
    body,
  });
}

export async function updateSectorPackage(
  id: string,
  body: UpdateSectorPackageRequest,
): Promise<{ package: SectorPackageSummary }> {
  return api<{ package: SectorPackageSummary }>(`/admin/sector-packages/${id}`, {
    method: "PATCH",
    body,
  });
}

export async function publishSectorPackage(id: string): Promise<{ package: SectorPackageSummary }> {
  return api<{ package: SectorPackageSummary }>(`/admin/sector-packages/${id}/publish`, {
    method: "POST",
  });
}

export async function archiveSectorPackage(id: string): Promise<{ package: SectorPackageSummary }> {
  return api<{ package: SectorPackageSummary }>(`/admin/sector-packages/${id}/archive`, {
    method: "POST",
  });
}

export async function deleteSectorPackage(id: string): Promise<void> {
  await api(`/admin/sector-packages/${id}`, { method: "DELETE" });
}

export async function upgradeSectorPackage(
  id: string,
  body: UpgradeSectorPackageRequest,
): Promise<UpgradeSectorPackageResponse> {
  return api<UpgradeSectorPackageResponse>(`/admin/sector-packages/${id}/upgrade`, {
    method: "POST",
    body,
  });
}

// Wizard endpoint — published paketler by parent_framework_version_id
export async function listPublishedSectorPackages(
  frameworkVersionId: string,
): Promise<PublishedSectorPackagesResponse> {
  return api<PublishedSectorPackagesResponse>(
    `/sector-packages/published?frameworkVersionId=${frameworkVersionId}`,
  );
}
